import React, { useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Timer, ArrowBack, Delete, Business } from "@material-ui/icons";
import SaveIcon from "@material-ui/icons/Save";
import { FormikProvider, useFormik } from "formik";
import "react-tabs/style/react-tabs.css";
import styled, { keyframes } from "styled-components";
import * as yup from "yup";
import fetchClient from "../../../api/fetchClient";
import Tabs from "../../../components/senior/wms/Tabs";
import message from "../../../helper/message";
import LogsMetadataModal from "../../../components/concur/LogsMetadataModal";
import axios from "axios";
import StorageIcon from "@mui/icons-material/Storage";

const metadataPath = process.env.REACT_APP_METADATA_PATH;

const validationSchema = yup.object({
    grupoId: yup.string().required("Grupo é obrigatório"),
    cnpj: yup
        .string()
        .required("CNPJ é obrigatório")
        .matches(/^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/, "CNPJ formato inválido"),
    name: yup.string("Informe o nome da empresa").required("Nome é obrigatório"),
    description: yup
        .string("Informe a descrição da empresa")
        .required("Descrição é obrigatório"),
    login: yup.string("Informe o login do sap").required("Login é obrigatório"),
    password: yup.string("Informe a senha do sap").required("Password é obrigatório"),
    database: yup.string("Informe o banco de dados").required("Banco de dados é obrigatório"),
    url: yup
        .string("Informe a url da service layer")
        .required("Url da service layer é obrigatório"),
});

function WmsFomPage(props) {
    const { data, isViewing } = props;

    const [empresa, setEmpresa] = useState(data);
    const [connected, setConnected] = useState(data.connected);
    const [connectedHana, setConnectedHana] = useState(data.connectedHana);
    const [isLoading, setIsLoading] = useState(false); // usado também em testes de conexão
    const [isLoadingToken, setIsLoadingToken] = useState(false);
    const [metadataCreationId, setMetadataCreationId] = useState(null);
    const [loadingMetadataCreation, setLoadingMetadataCreation] = useState(false);

    // ===== API =====
    const save = async (empresa) => {
        console.log("Iniciando salvar:", empresa);
        await fetchClient.post("/wms/company", {
            ...empresa,
            connected,
        });
    };

    const onRetrieveLogs = async () => {
        try {
            const { data } = await axios.get(
                `${metadataPath}/metadata/${metadataCreationId}?type=senior`
            );
            return data;
        } catch (error) {
            message.error(error.message);
        }
    };

    // ===== Helpers =====
    const isValidSL = (u) => /^https?:\/\/.+\/b1s\/v1$/i.test(u?.trim());
    const isValidHana = (u) => /^https?:\/\/[^ ]+:\d{2,5}$/i.test(u?.trim());

    const touchAllAndReport = async () => {
        const errs = await formik.validateForm();
        if (Object.keys(errs).length) {
            formik.setTouched(
                Object.keys(validationSchema.fields).reduce(
                    (acc, k) => ({ ...acc, [k]: true }),
                    {}
                ),
                true
            );
            message.error("Corrija os campos destacados antes de continuar.");
            return false;
        }
        return true;
    };

    const confirmDialog = async (texto) => window.confirm(texto);

    // ===== Ações =====
    const onSave = async () => {
        try {
            const ok = await touchAllAndReport();
            if (!ok) return;

            setIsLoading(true);
            const body = {
                id: formik.values.id,
                enabled: !!formik.values.enabled,
                cnpj: formik.values.cnpj,
                inscricao_estadual: formik.values.inscricao_estadual,
                grupoId: formik.values.grupoId,
                name: formik.values.name,
                description: formik.values.description,
                login: formik.values.login,
                password: formik.values.password,
                database: formik.values.database,
                urlHanaSql: formik.values.urlHanaSql,
                hPassword: formik.values.hPassword,
                hUserName: formik.values.hUserName,
                url: formik.values.url,
                ativo: formik.values.ativo || false,
                metadata: formik.values.metadata || false,
                wms_token: formik.values.wms_token,
                token: formik.values.token,
                connectedHana: connectedHana,
                ftp_user: formik.values.ftp_user,
                ftp_password: formik.values.ftp_password,
                ftp_host: formik.values.ftp_host,
                ftp_port: formik.values.ftp_port,
                ftp_enabled: formik.values.ftp_enabled,
                ftp_folder: formik.values.ftp_folder,
                cnpj_unidade: formik.values.cnpj_unidade,
                email_notification: formik.values.email_notification,
                // wms_url não está sendo enviado no seu backend; mantive seu payload original
            };

            await save(body);
            isViewing(false);
            message.sucess("Salvo com sucesso!");
        } catch (error) {
            console.error("Erro ao salvar:", error);
            message.error(error.response?.data?.message || "Erro ao salvar.");
        } finally {
            setIsLoading(false);
        }
    };

    const onDelete = async (id) => {
        try {
            if (!id) {
                return message.error("Registro sem ID. Salve primeiro para poder excluir.");
            }
            const confirmar = await confirmDialog("Deseja realmente excluir este grupo?");
            if (!confirmar) return;

            await fetchClient.delete(`/wms/${id}`);
            isViewing(false);
            message.sucess("Registro deletado com sucesso");
        } catch (error) {
            console.error("Erro ao deletar:", error);
            message.error(error.response?.data?.message || "Erro ao deletar.");
        }
    };

    const onGenerateToken = async () => {
        try {
            setIsLoadingToken(true);

            if (formik.values.token) {
                const confirma = await confirmDialog(
                    "Já existe um token gerado para esta empresa. Deseja gerar um novo? O anterior será substituído."
                );
                if (!confirma) {
                    setIsLoadingToken(false);
                    return;
                }
            }

            const empresa = { id: formik.values.id };
            if (!empresa.id) {
                setIsLoadingToken(false);
                return message.error("Salve a empresa antes de gerar o token.");
            }

            const { data } = await fetchClient.post("wms/aplicacao/token", empresa);

            message.sucess("Token gerado com sucesso !");
            const { message: response } = data;
            formik.setFieldValue("token", response.token);
        } catch (error) {
            console.log(error);
            message.error(error.response?.data?.message || "Erro ao gerar token.");
        } finally {
            setIsLoadingToken(false);
        }
    };

    const onInitializeMetadataCreation = async (id) => {
        try {
            if (!id) {
                const ok = await touchAllAndReport();
                if (!ok) return;
                return message.error("Salve a empresa antes de criar a metadata.");
            }

            if (metadataCreationId) {
                const prosseguir = await confirmDialog(
                    "Já existe uma criação de metadata em andamento para este registro. Deseja iniciar outra?"
                );
                if (!prosseguir) return;
            }

            setLoadingMetadataCreation(true);
            const { data } = await axios.post(`${metadataPath}/metadata`, {
                company: id,
                type: "senior",
            });
            setMetadataCreationId(data.uuid);
            message.sucess("Criação de metadata iniciada.");
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoadingMetadataCreation(false);
        }
    };

    const onTestConnectSL = async () => {
        try {

            setIsLoading(true);
            const empresa = { id: formik.values.id };
            await fetchClient.post("wms/testConnectionSap", empresa);
            message.sucess("Conectado com sucesso");
            formik.setFieldValue("connected", true);
            setConnected(true);
            setEmpresa((v) => ({ ...v, connected: true }));
        } catch (error) {
            formik.setFieldValue("connected", false);
            setConnected(false);
            setEmpresa((v) => ({ ...v, connected: false }));
            console.log(error);
            message.error(error.response?.data?.message || "Erro ao conectar.");
        } finally {
            setIsLoading(false);
        }
    };

    const onTestConnectHana = async () => {
        try {

            setIsLoading(true);
            const empresa = { id: formik.values.id };
            await fetchClient.post("wms/connectHanaSQL", empresa);
            message.sucess("Conectado com sucesso");
            formik.setFieldValue("connectedHana", true);
            setConnectedHana(true);
            setEmpresa((v) => ({ ...v, connectedHana: true }));
        } catch (error) {
            formik.setFieldValue("connectedHana", false);
            setConnectedHana(false);
            setEmpresa((v) => ({ ...v, connectedHana: false }));
            console.log(error);
            message.error(error.response?.data?.message || "Erro ao conectar.");
        } finally {
            setIsLoading(false);
        }
    };

    // ===== Defaults & Formik =====
    const defaultValues = {
        id: empresa.id || "",
        enabled: empresa.enabled ?? true,
        cnpj: empresa.cnpj || "",
        inscricao_estadual: empresa.inscricao_estadual || "",
        name: empresa.name || "",
        grupoId: empresa.grupoId || "",
        description: empresa.description || "",
        login: empresa.login || "",
        password: empresa.password || "",
        database: empresa.database || "",
        urlHanaSql: empresa.urlHanaSql || "",
        hPassword: empresa.hPassword || "",
        hUserName: empresa.hUserName || "",
        url: empresa.url || "",
        ativo: empresa.ativo || false,
        metadata: empresa.metadata || false,
        wms_token: empresa.wms_token || "",
        token: empresa.token || "",
        ftp_user: empresa.ftp_user || "",
        ftp_password: empresa.ftp_password || "",
        ftp_host: empresa.ftp_host || "",
        ftp_port: empresa.ftp_port || "",
        ftp_enabled: empresa.ftp_enabled ?? true,
        ftp_folder: empresa.ftp_folder || "",
        cnpj_unidade: empresa.cnpj_unidade || "",
        wms_url: empresa.wms_url || "",
        email_notification: empresa.email_notification || "",
    };

    const formik = useFormik({
        initialValues: defaultValues,
        validationSchema: validationSchema,
        enableReinitialize: true,
        validateOnMount: true,
        onSubmit: async (values) => {
            console.log("Submetendo valores:", values);
            await onSave(values);
        },
    });

    const {
        touched,
        values,
        errors,
        handleChange,
        isSubmitting,
        isValid,
        dirty,
    } = formik;

    return (
        <FormikProvider value={formik}>
            <Container>
                {metadataCreationId && (
                    <LogsMetadataModal
                        show={(bool) => bool === false && setMetadataCreationId(null)}
                        onRetrieveLogs={onRetrieveLogs}
                    />
                )}
                <PageContent>
                    <ModernHeader>
                        <HeaderLeft>
                            <BackButton onClick={() => isViewing(false)}>
                                <ArrowBack />
                            </BackButton>
                            <HeaderContent>
                                <PageTitle>
                                    <Business style={{ marginRight: '12px', fontSize: '32px' }} />
                                    {empresa.newBusiness ? 'Nova Aplicação Senior WMS' : 'Editar Aplicação'}
                                </PageTitle>
                                <PageSubtitle>
                                    {empresa.newBusiness ? 'Cadastre uma nova aplicação no sistema' : 'Gerencie as informações da aplicação'}
                                </PageSubtitle>
                            </HeaderContent>
                        </HeaderLeft>
                    </ModernHeader>

                    <ActionsCard>
                        <ActionsGrid>
                            <ActionButton
                                variant="danger"
                                disabled={!empresa.id || isLoading || isLoadingToken || loadingMetadataCreation}
                                onClick={async () => await onDelete(empresa.id)}
                            >
                                <Delete style={{ marginRight: '8px', fontSize: '20px' }} />
                                Excluir
                            </ActionButton>

                            <ActionButton
                                variant="primary"
                                onClick={onSave}
                                disabled={isSubmitting || (isLoading && !values.id) || (!dirty && !values.id)}
                            >
                                {isSubmitting || (isLoading && !values.id) ? (
                                    <CircularProgress size={20} style={{ color: 'white' }} />
                                ) : (
                                    <>
                                        <SaveIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                                        Salvar
                                    </>
                                )}
                            </ActionButton>

                            <ActionButton
                                variant="primary"
                                onClick={async () => await onInitializeMetadataCreation(empresa.id)}
                                disabled={loadingMetadataCreation || isSubmitting}
                            >
                                {loadingMetadataCreation ? (
                                    <CircularProgress size={20} style={{ color: 'white' }} />
                                ) : (
                                    <>
                                        <StorageIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                                        Criar Metadata
                                    </>
                                )}
                            </ActionButton>

                            <ActionButton
                                variant="primary"
                                onClick={onGenerateToken}
                                disabled={isLoadingToken || isSubmitting}
                            >
                                {isLoadingToken ? (
                                    <CircularProgress size={20} style={{ color: 'white' }} />
                                ) : (
                                    <>
                                        <Timer style={{ marginRight: '8px', fontSize: '20px' }} />
                                        Gerar Token
                                    </>
                                )}
                            </ActionButton>
                        </ActionsGrid>
                    </ActionsCard>

                    <FormCard>
                        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
                            <Tabs
                                values={values}
                                touched={touched}
                                errors={errors}
                                handleChange={handleChange}
                                onTestConnect={onTestConnectSL}
                                onTestConnectHana={onTestConnectHana}
                                connected={connected}
                                connectedHana={connectedHana}
                                isLoading={isLoading}
                                isLoadingHana={isLoading}
                            />
                        </Form>
                    </FormCard>
                </PageContent>
            </Container>
        </FormikProvider>
    );
}

export default WmsFomPage;

// ===== STYLED COMPONENTS =====

// Animacoes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 65px);
  background-color: #f3f4f6;
  font-family: 'Mulish', sans-serif;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const PageContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
  padding: 3rem 2rem;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const ModernHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  animation: ${fadeIn} 0.6s ease;
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 6px 0;
  display: flex;
  align-items: center;
  font-family: 'Mulish', sans-serif;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #7f8c8d;
  margin: 0;
  font-family: 'Mulish', sans-serif;
`;

const BackButton = styled.button`
  background: rgba(132, 204, 22, 0.1);
  border: none;
  border-radius: 12px;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #84cc16;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(132, 204, 22, 0.2);
    transform: translateX(-4px);
  }

  &:active {
    transform: translateX(0);
  }
`;

const ActionsCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.5s ease 0.1s both;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionButton = styled.button`
  background: ${props => {
    if (props.variant === 'danger') return 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)';
    if (props.variant === 'success') return 'linear-gradient(135deg, #27ae60 0%, #229954 100%)';
    if (props.variant === 'warning') return 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)';
    return 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)';
  }};
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-family: 'Mulish', sans-serif;
  box-shadow: 0 2px 8px ${props => {
    if (props.variant === 'danger') return 'rgba(231, 76, 60, 0.3)';
    if (props.variant === 'success') return 'rgba(39, 174, 96, 0.3)';
    if (props.variant === 'warning') return 'rgba(243, 156, 18, 0.3)';
    return 'rgba(132, 204, 22, 0.3)';
  }};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => {
      if (props.variant === 'danger') return 'rgba(231, 76, 60, 0.4)';
      if (props.variant === 'success') return 'rgba(39, 174, 96, 0.4)';
      if (props.variant === 'warning') return 'rgba(243, 156, 18, 0.4)';
      return 'rgba(132, 204, 22, 0.4)';
    }};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
    box-shadow: none;
    opacity: 0.6;
  }
`;

const FormCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.5s ease 0.2s both;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #d0d0d0;
  }
`;

const Form = styled.form`
  width: 100%;
`;
